import React, { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import JanesChatbot from '../components/JanesChatbot'
import Sidebar from '../components/Sidebar'
import Header from '../components/Header'
import { Z_INDEX } from '@/lib/constants/z-index'

interface AuthLayoutProps {
  children: React.ReactNode
}

/**
 * Authenticated App Layout
 *
 * Provides full application chrome for authenticated users:
 * - Header (top bar) with navigation controls and user menu
 * - Sidebar (desktop) with application navigation
 * - Mobile sidebar drawer that slides in from left
 * - Main content area with responsive padding
 * - Janes chatbot integration
 *
 * This layout is used for all authenticated routes (/, /dashboard, /employees, etc.)
 */
export function AuthLayout({ children }: AuthLayoutProps) {
  const { user, logout } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [janesChatbotOpen, setJanesChatbotOpen] = useState(false)

  const handleLogout = async () => {
    try {
      await logout()
      // Redirect is now handled in the auth context
    } catch (error) {
      console.error('Error during logout:', error)
      // Still attempt to redirect even if there's an error
      window.location.href = '/login'
    }
  }

  return (
    <div className="min-h-screen bg-brand-dark flex relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/10 via-transparent to-brand-blue/5" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-primary/50 to-transparent" />
      </div>

      {/* Sidebar - Always visible on desktop, toggleable on mobile */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        user={user}
        onJanesChatbotOpen={() => setJanesChatbotOpen(true)}
      />

      {/* Main Content */}
      <div className="flex-1 min-w-0 overflow-x-hidden lg:ml-0 relative z-10">
        {/* Header */}
        <Header
          onMobileMenuOpen={() => setSidebarOpen(true)}
          user={user}
          onLogout={handleLogout}
        />

        {/* Page Content */}
        <main className="flex-1 p-4 sm:p-6 min-w-0 overflow-x-hidden">
          <div className="animate-fade-in-up">
            {children}
          </div>
        </main>
      </div>

      {/* Sidebar Overlay - Enhanced with backdrop blur */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm lg:hidden animate-fade-in"
          style={{ zIndex: Z_INDEX.SIDEBAR_BACKDROP }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Janes Chatbot */}
      <JanesChatbot
        isOpen={janesChatbotOpen}
        onClose={() => setJanesChatbotOpen(false)}
      />
    </div>
  )
}
